"use client";

import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import type { PlaceResult } from "@/lib/api/misc";
import type { KundaliLang, KundaliTranslations } from "./KundaliFormCard.i18n";
import { formatBirthDateDisplayInput } from "./KundaliFormCard.validation";

interface FieldWrapperProps {
  activeField: string | null;
  fieldName: string;
  children: React.ReactNode;
}

export function FieldWrapper({ activeField, fieldName, children }: FieldWrapperProps) {
  return (
    <div
      style={{
        opacity: activeField && activeField !== fieldName ? 0.4 : 1,
        transition: "opacity 0.3s ease",
      }}
    >
      {children}
    </div>
  );
}

interface FieldHintProps {
  language: KundaliLang;
  en: string;
  hi: string;
}

export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p
      id={id}
      className="mt-1.5 text-[12px] font-medium"
      style={{ color: "var(--color-error-text)" }}
    >
      {message}
    </p>
  );
}

function FieldHint({ language, en, hi }: FieldHintProps) {
  return (
    <p className="text-[11px] mb-2" style={{ color: "var(--ish-text-muted)" }}>
      {language === "hi" ? hi : en}
    </p>
  );
}

// --- NameField ---

interface NameFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  activeField: string | null;
  language: KundaliLang;
  t: KundaliTranslations;
  error?: string;
}

export function NameField({
  value,
  onChange,
  onFocus,
  onBlur,
  activeField,
  language,
  t,
  error,
}: NameFieldProps) {
  return (
    <FieldWrapper activeField={activeField} fieldName="name">
      <label
        htmlFor="name"
        className="block text-[13px] font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.name}
      </label>
      {activeField === "name" && (
        <FieldHint
          language={language}
          hi="आपका नाम हमारे ज्योतिषी को आपके फल कथन को व्यक्तिगत बनाने में मदद करता है"
          en="Your name helps our Jyotishi personalize your reading"
        />
      )}
      <input
        id="name"
        type="text"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={t.namePlaceholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "name-error" : undefined}
        className="input-sacred w-full rounded-lg px-4 py-3 text-[14px]"
        style={{ color: "var(--ish-dark)" }}
      />
      <FieldError id="name-error" message={error} />
    </FieldWrapper>
  );
}

// --- BirthDateField ---

interface BirthDateFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  activeField: string | null;
  language: KundaliLang;
  t: KundaliTranslations;
  error?: string;
}

export function BirthDateField({
  value,
  onChange,
  onFocus,
  onBlur,
  activeField,
  language,
  t,
  error,
}: BirthDateFieldProps) {
  return (
    <FieldWrapper activeField={activeField} fieldName="birthDate">
      <label
        htmlFor="birthDate"
        className="block text-[13px] font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.birthDate}
      </label>
      {activeField === "birthDate" && (
        <FieldHint
          language={language}
          hi="सटीक जन्म तिथि से ग्रहों की स्थिति का सही पता चलता है"
          en="The exact date reveals the true alignment of the stars"
        />
      )}
      <input
        id="birthDate"
        type="text"
        inputMode="numeric"
        required
        value={value}
        onChange={(e) => onChange(formatBirthDateDisplayInput(e.target.value))}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder="dd/mm/yyyy"
        maxLength={10}
        autoComplete="bday"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "birthDate-error" : undefined}
        className="input-sacred w-full rounded-lg px-4 py-3 text-[14px]"
        style={{ color: "var(--ish-dark)" }}
      />
      <FieldError id="birthDate-error" message={error} />
    </FieldWrapper>
  );
}

// --- BirthTimeField ---

interface BirthTimeFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  activeField: string | null;
  language: KundaliLang;
  t: KundaliTranslations;
  required?: boolean;
  error?: string;
}

export function BirthTimeField({
  value,
  onChange,
  onFocus,
  onBlur,
  activeField,
  language,
  t,
  required = false,
  error,
}: BirthTimeFieldProps) {
  return (
    <FieldWrapper activeField={activeField} fieldName="birthTime">
      <label
        htmlFor="birthTime"
        className="block text-[13px] font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.birthTime}{" "}
        {!required && (
          <span className="font-normal" style={{ color: "var(--ish-text-faint)" }}>
            {t.optional}
          </span>
        )}
      </label>
      {activeField === "birthTime" && (
        <FieldHint
          language={language}
          hi="समय से आपका लग्न (Lagna) और सटीक भाव फल निर्धारित होता है"
          en="Samay se aapka Lagna aur sahi bhav fal nishchit hota hai"
        />
      )}
      <input
        id="birthTime"
        type="time"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "birthTime-error" : undefined}
        className="input-sacred w-full rounded-lg px-4 py-3 text-[14px]"
        style={{ color: "var(--ish-dark)" }}
      />
      <FieldError id="birthTime-error" message={error} />
    </FieldWrapper>
  );
}

// --- BirthPlaceField ---

interface BirthPlaceFieldProps {
  value: string;
  onChange: (v: string) => void;
  onSelect: (place: PlaceResult) => void;
  onFocus: () => void;
  onBlur: () => void;
  activeField: string | null;
  language: KundaliLang;
  t: KundaliTranslations;
  error?: string;
}

export function BirthPlaceField({
  value,
  onChange,
  onSelect,
  onFocus,
  onBlur,
  activeField,
  language,
  t,
  error,
}: BirthPlaceFieldProps) {
  return (
    <FieldWrapper activeField={activeField} fieldName="birthPlace">
      <label
        htmlFor="birthPlace"
        className="block text-[13px] font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.birthPlace}
      </label>
      {activeField === "birthPlace" && (
        <FieldHint
          language={language}
          hi="स्थान से हम आपकी जन्म कुंडली के लिए सटीक समय क्षेत्र (Timezone) प्राप्त करते हैं"
          en="Location gives us the exact coordinates and timezone for your chart"
        />
      )}
      <div onFocus={onFocus} onBlur={onBlur}>
        <PlaceAutocomplete
          id="birthPlace"
          value={value}
          onChange={onChange}
          onSelect={onSelect}
          placeholder={t.placePlaceholder}
          invalid={Boolean(error)}
          describedBy={error ? "birthPlace-error" : undefined}
        />
      </div>
      <FieldError id="birthPlace-error" message={error} />
    </FieldWrapper>
  );
}
